import { useEffect, useState, useCallback, useRef } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Wrench, Plus, X, Trash2, Camera, MapPin, PenLine, Cpu, Building2, Truck, Calendar, Loader2, ArrowRightLeft, FileDown, Send, Rocket, PlayCircle } from "lucide-react";
import BrandLoader from "@/components/BrandLoader";
import InstallWizard from "@/components/InstallWizard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fileUrl = (p) => `${BACKEND_URL}/api/files/${p}`;

const TYPES = [
  { v: "nouvelle_installation", l: "Nouvelle installation", c: "#10B981" },
  { v: "sav", l: "SAV / Dépannage", c: "#F5821F" },
  { v: "transfert", l: "Transfert de balise", c: "#A855F7" },
];
const STATUS = [
  { v: "planifie", l: "Planifié", c: "#6b7280" },
  { v: "en_cours", l: "En cours", c: "#17A2B8" },
  { v: "termine", l: "Terminé", c: "#10B981" },
];
const meta = (arr, v) => arr.find((x) => x.v === v) || { l: v, c: "#6b7280" };
const inp = "w-full bg-secondary/40 border border-border rounded-md px-3 py-2.5 text-sm outline-none focus:border-[#17A2B8]";
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { timeZone: "Pacific/Noumea", day: "2-digit", month: "short", year: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—");

const Badge = ({ item }) => (
  <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold border" style={{ color: item.c, background: `${item.c}1f`, borderColor: `${item.c}55` }}>{item.l}</span>
);

const emptyForm = () => ({
  type: "nouvelle_installation", status: "planifie", technician_name: "", company_id: "", device_id: "",
  vehicle_id: "", source_vehicle_id: "", dest_vehicle_id: "", date: "", notes: "", photos: [], signature: null, location: null,
});

// ---- Pad de signature ----
function SignaturePad({ value, onChange }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [uploading, setUploading] = useState(false);

  const pos = (e) => {
    const c = canvasRef.current; const r = c.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: (t.clientX - r.left) * (c.width / r.width), y: (t.clientY - r.top) * (c.height / r.height) };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; const ctx = canvasRef.current.getContext("2d"); const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
  const move = (e) => { if (!drawing.current) return; e.preventDefault(); const ctx = canvasRef.current.getContext("2d"); const p = pos(e); ctx.lineWidth = 2.5; ctx.lineCap = "round"; ctx.strokeStyle = "#0f1115"; ctx.lineTo(p.x, p.y); ctx.stroke(); };
  const end = () => { drawing.current = false; };
  const clear = () => { const c = canvasRef.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); onChange(null); };
  const save = () => {
    const c = canvasRef.current;
    c.toBlob(async (blob) => {
      if (!blob) return;
      setUploading(true);
      try {
        const fd = new FormData();
        fd.append("file", new File([blob], "signature.png", { type: "image/png" }));
        const r = await api.post("/upload", fd);
        onChange({ path: r.data.path, url: r.data.url });
        toast.success("Signature enregistrée");
      } catch { toast.error("Échec de l'envoi de la signature"); }
      setUploading(false);
    }, "image/png");
  };

  return (
    <div data-testid="signature-pad">
      <div className="rounded-md border border-border bg-white overflow-hidden">
        <canvas ref={canvasRef} width={600} height={200} className="w-full touch-none" style={{ height: 150 }}
          onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={move} onTouchEnd={end} data-testid="signature-canvas" />
      </div>
      <div className="flex gap-2 mt-2">
        <button type="button" onClick={clear} data-testid="signature-clear" className="text-xs px-3 py-1.5 rounded-md border border-border hover:bg-secondary/60">Effacer</button>
        <button type="button" onClick={save} disabled={uploading} data-testid="signature-save" className="text-xs px-3 py-1.5 rounded-md bg-[#17A2B8] text-white font-semibold flex items-center gap-1 disabled:opacity-50">{uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PenLine className="w-3.5 h-3.5" />} Valider la signature</button>
        {value?.url && <span className="text-xs text-[#10B981] self-center">✓ Signée</span>}
      </div>
    </div>
  );
}

export default function InterventionsPage() {
  const { user, can } = useAuth();
  const canWrite = user?.role !== "client" && (user?.role === "admin" || can("interventions"));   // client = toujours lecture seule
  const [rows, setRows] = useState([]);
  const [refs, setRefs] = useState({ companies: [], devices: [], vehicles: [] });
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [geoLoading, setGeoLoading] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [resumeId, setResumeId] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [viewRow, setViewRow] = useState(null);   // détail lecture seule (client)

  const loadDrafts = useCallback(async () => {
    try {
      const r = await api.get("/installations");
      setDrafts((r.data || []).filter((d) => !(d.install || {}).finalized && d.status !== "termine"));
    } catch { setDrafts([]); }
  }, []);
  useEffect(() => { loadDrafts(); }, [loadDrafts]);
  const openWizard = (id = null) => { setResumeId(id); setWizardOpen(true); };
  const onWizardFinished = () => { load(); loadDrafts(); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (canWrite) {
        // /interventions/refs est protégé par la permission d'écriture -> ne pas l'appeler pour un client (évite 403)
        const [a, b] = await Promise.all([api.get("/interventions"), api.get("/interventions/refs")]);
        setRows(a.data); setRefs(b.data);
      } else {
        const a = await api.get("/interventions");
        setRows(a.data);
      }
    } catch { toast.error("Chargement impossible"); }
    setLoading(false);
  }, [canWrite]);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openCreate = () => { setEditId(null); setForm({ ...emptyForm(), technician_name: user?.name || "" }); setModal(true); };
  const openEdit = (r) => {
    setEditId(r.id);
    setForm({
      type: r.type || "nouvelle_installation", status: r.status || "planifie", technician_name: r.technician_name || "",
      company_id: r.company_id || "", device_id: r.device_id || "", vehicle_id: r.vehicle_id || "",
      source_vehicle_id: r.source_vehicle_id || "", dest_vehicle_id: r.dest_vehicle_id || "",
      date: r.date ? r.date.slice(0, 16) : "", notes: r.notes || "", photos: r.photos || [], signature: r.signature || null, location: r.location || null,
    });
    setModal(true);
  };

  const addPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploadingPhoto(true);
    for (const file of files) {
      try {
        const fd = new FormData(); fd.append("file", file);
        const r = await api.post("/upload", fd);
        setForm((f) => ({ ...f, photos: [...f.photos, { path: r.data.path, url: r.data.url, name: r.data.name }] }));
      } catch { toast.error(`Échec envoi ${file.name}`); }
    }
    setUploadingPhoto(false);
    e.target.value = "";
  };
  const removePhoto = (idx) => setForm((f) => ({ ...f, photos: f.photos.filter((_, i) => i !== idx) }));

  const grabLocation = () => {
    if (!navigator.geolocation) { toast.error("Géolocalisation non disponible"); return; }
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(async (p) => {
      const loc = { lat: p.coords.latitude, lon: p.coords.longitude, address: null };
      try { const r = await api.get(`/geocode/reverse?lat=${loc.lat}&lon=${loc.lon}`); loc.address = r.data?.address || null; } catch { /* ignore */ }
      set("location", loc); setGeoLoading(false); toast.success("Position enregistrée");
    }, () => { setGeoLoading(false); toast.error("Position refusée"); }, { enableHighAccuracy: true, timeout: 10000 });
  };

  const save = async () => {
    if (form.type === "transfert" && (!form.source_vehicle_id || !form.dest_vehicle_id)) { toast.error("Renseignez le véhicule démonté et le véhicule remonté"); return; }
    setSaving(true);
    const payload = {
      ...form,
      date: form.date ? new Date(form.date).toISOString() : null,
      company_id: form.company_id || null, device_id: form.device_id || null,
      vehicle_id: form.vehicle_id || null, source_vehicle_id: form.source_vehicle_id || null, dest_vehicle_id: form.dest_vehicle_id || null,
    };
    try {
      if (editId) await api.put(`/interventions/${editId}`, payload);
      else await api.post("/interventions", payload);
      toast.success(editId ? "Intervention mise à jour" : "Intervention créée");
      setModal(false); load();
    } catch { toast.error("Enregistrement impossible"); }
    setSaving(false);
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer cette intervention ?")) return;
    try { await api.delete(`/interventions/${id}`); setRows((r) => r.filter((x) => x.id !== id)); toast.success("Supprimée"); }
    catch { toast.error("Suppression impossible"); }
  };

  const dev = refs.devices.find((d) => d.id === form.device_id);

  const pdfHref = (id) => `${BACKEND_URL}/api/interventions/${id}/pdf`;
  const sendReport = async (r, retryEmail = null) => {
    try {
      const res = await api.post(`/interventions/${r.id}/send`, { base_url: BACKEND_URL, ...(retryEmail ? { email: retryEmail } : {}) });
      toast.success(`Bon d'intervention envoyé à ${res.data.sent_to}`);
    } catch (e) {
      if (e?.response?.status === 400 && !retryEmail) {
        const email = window.prompt("Aucun email client enregistré. Saisissez l'adresse email du client :", "");
        if (email) sendReport(r, email);
      } else { toast.error("Envoi impossible"); }
    }
  };

  return (
    <Layout>
      <div className="max-w-5xl mx-auto" data-testid="interventions-page">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2"><Wrench className="w-6 h-6 brand-teal" /> Interventions</h1>
            <p className="text-sm text-muted-foreground">Installation, SAV et transfert de balises</p>
          </div>
          <button onClick={openCreate} data-testid="new-intervention-btn" className={`flex items-center gap-2 bg-[#F5821F] text-white font-semibold px-4 py-2.5 rounded-md text-sm hover:opacity-90 ${canWrite ? "" : "hidden"}`}>
            <Plus className="w-4 h-4" /> Nouvelle
          </button>
        </div>

        {/* P0.6a — Installation technique réservée admin/technicien (install_tech). Invisible pour le client. */}
        {can("install_tech") && (
          <button onClick={() => openWizard(null)} data-testid="new-installation-btn"
            className="w-full mb-4 flex items-center justify-center gap-2 bg-[#17A2B8] text-white font-bold py-4 rounded-xl text-base hover:opacity-90 active:scale-[0.99] transition-transform">
            <Rocket className="w-5 h-5" /> + Nouvelle installation
          </button>
        )}

        {can("install_tech") && drafts.length > 0 && (
          <div className="mb-5" data-testid="install-drafts">
            <p className="label-mono mb-2">Installations en cours ({drafts.length})</p>
            <div className="grid gap-2 sm:grid-cols-2">
              {drafts.map((d) => (
                <button key={d.id} onClick={() => openWizard(d.id)} data-testid={`resume-install-${d.id}`}
                  className="flex items-center justify-between gap-2 border border-[#17A2B8]/40 bg-[#17A2B8]/5 rounded-lg px-3 py-3 text-left hover:bg-[#17A2B8]/10">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold truncate">{(d.install || {}).unit_name || d.company_name || "Brouillon"}</p>
                    <p className="text-xs text-muted-foreground truncate">{d.company_name || ""}{(d.install || {}).imei ? ` · ${(d.install).imei}` : ""} · {d.status === "a_controler" ? "À contrôler" : "Brouillon"}</p>
                  </div>
                  <span className="flex items-center gap-1 text-[#17A2B8] text-xs font-bold shrink-0"><PlayCircle className="w-4 h-4" /> Reprendre</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-16"><BrandLoader label="Chargement des interventions…" /></div>
        ) : rows.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground" data-testid="interventions-empty">
            <Wrench className="w-10 h-10 mx-auto mb-3 opacity-40" />
            {canWrite ? "Aucune intervention. Créez-en une avec le bouton « Nouvelle »." : "Aucune intervention pour votre société."}
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2" data-testid="interventions-list">
            {rows.map((r) => (
              <div key={r.id} className="glass rounded-lg p-4 border border-border" data-testid={`intervention-${r.id}`}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex flex-wrap gap-1.5"><Badge item={meta(TYPES, r.type)} /><Badge item={meta(STATUS, r.status)} /></div>
                  <div className="flex items-center gap-0.5">
                    <button onClick={() => window.open(pdfHref(r.id), "_blank")} title="Bon d'intervention PDF" data-testid={`pdf-intervention-${r.id}`} className="text-muted-foreground hover:text-[#17A2B8] p-1"><FileDown className="w-4 h-4" /></button>
                    {canWrite && <button onClick={() => sendReport(r)} title="Envoyer au client" data-testid={`send-intervention-${r.id}`} className="text-muted-foreground hover:text-[#F5821F] p-1"><Send className="w-4 h-4" /></button>}
                    {canWrite && <button onClick={() => del(r.id)} data-testid={`del-intervention-${r.id}`} className="text-muted-foreground hover:text-destructive p-1"><Trash2 className="w-4 h-4" /></button>}
                  </div>
                </div>
                {/* P0.6a — une demande client "nouvelle_installation" peut être lancée en installation technique */}
                {can("install_tech") && r.type === "nouvelle_installation" && !(r.install || {}).finalized && (
                  <button onClick={() => openWizard(r.id)} data-testid={`start-install-${r.id}`}
                    className="w-full mb-2 flex items-center justify-center gap-2 bg-[#F5821F] text-white font-bold py-2.5 rounded-md text-sm hover:opacity-90">
                    <Rocket className="w-4 h-4" /> Démarrer l'installation
                  </button>
                )}
                <button onClick={() => (canWrite ? openEdit(r) : setViewRow(r))} className="text-left w-full" data-testid={`open-intervention-${r.id}`}>
                  <div className="space-y-1 text-sm">
                    {r.company_name && <div className="flex items-center gap-2"><Building2 className="w-3.5 h-3.5 text-muted-foreground" /> {r.company_name}</div>}
                    {(r.device_imei || r.device_name) && <div className="flex items-center gap-2"><Cpu className="w-3.5 h-3.5 text-muted-foreground" /> {r.device_name || "Balise"} {r.device_imei ? `· ${r.device_imei}` : ""}</div>}
                    {r.type === "transfert" ? (
                      <div className="flex items-center gap-2 flex-wrap"><ArrowRightLeft className="w-3.5 h-3.5 text-muted-foreground" /> {r.source_vehicle_name || "?"} → {r.dest_vehicle_name || "?"}</div>
                    ) : r.vehicle_name ? (
                      <div className="flex items-center gap-2"><Truck className="w-3.5 h-3.5 text-muted-foreground" /> {r.vehicle_name}</div>
                    ) : null}
                    <div className="flex items-center gap-2 text-muted-foreground text-xs"><Calendar className="w-3.5 h-3.5" /> {fmt(r.date)} · {r.technician_name || "—"}</div>
                  </div>
                  {(r.photos?.length || r.signature) && (
                    <div className="flex gap-1.5 mt-2">
                      {(r.photos || []).slice(0, 4).map((p, i) => <img key={i} src={fileUrl(p.path)} alt="" className="w-11 h-11 rounded object-cover border border-border" />)}
                      {r.signature?.path && <img src={fileUrl(r.signature.path)} alt="signature" className="w-11 h-11 rounded object-contain border border-border bg-white" />}
                    </div>
                  )}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {modal && canWrite && (
        <div className="fixed inset-0 z-[1000] bg-black/60 flex items-end sm:items-center justify-center" onClick={() => setModal(false)}>
          <div className="bg-background w-full sm:max-w-lg sm:rounded-xl rounded-t-2xl max-h-[92vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()} data-testid="intervention-modal">
            <div className="flex items-center justify-between mb-4 sticky top-0 bg-background pb-2">
              <h2 className="text-lg font-bold">{editId ? "Modifier l'intervention" : "Nouvelle intervention"}</h2>
              <button onClick={() => setModal(false)} data-testid="close-modal"><X className="w-5 h-5" /></button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="label-mono block mb-1.5">Type d'intervention</label>
                <div className="grid grid-cols-3 gap-2">
                  {TYPES.map((t) => (
                    <button key={t.v} onClick={() => set("type", t.v)} data-testid={`type-${t.v}`}
                      className={`text-xs font-semibold rounded-md px-2 py-2.5 border transition-colors ${form.type === t.v ? "text-white" : "text-foreground border-border hover:bg-secondary/50"}`}
                      style={form.type === t.v ? { background: t.c, borderColor: t.c } : {}}>{t.l}</button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label-mono block mb-1.5">Statut</label>
                  <select className={inp} value={form.status} onChange={(e) => set("status", e.target.value)} data-testid="f-status">
                    {STATUS.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label-mono block mb-1.5">Date</label>
                  <input type="datetime-local" className={inp} value={form.date} onChange={(e) => set("date", e.target.value)} data-testid="f-date" />
                </div>
              </div>

              <div>
                <label className="label-mono block mb-1.5">Technicien</label>
                <input className={inp} value={form.technician_name} onChange={(e) => set("technician_name", e.target.value)} placeholder="Nom du technicien" data-testid="f-technician" />
              </div>

              <div>
                <label className="label-mono block mb-1.5">Client</label>
                <select className={inp} value={form.company_id} onChange={(e) => set("company_id", e.target.value)} data-testid="f-company">
                  <option value="">— Sélectionner —</option>
                  {refs.companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>

              <div>
                <label className="label-mono block mb-1.5">Balise (boîtier)</label>
                <select className={inp} value={form.device_id} onChange={(e) => set("device_id", e.target.value)} data-testid="f-device">
                  <option value="">— Sélectionner —</option>
                  {refs.devices.map((d) => <option key={d.id} value={d.id}>{(d.name || d.model || "Balise")} · {d.imei}</option>)}
                </select>
                {dev && <p className="text-xs text-muted-foreground mt-1">IMEI : {dev.imei}{dev.model ? ` · ${dev.model}` : ""}</p>}
              </div>

              {form.type === "transfert" ? (
                <div className="grid grid-cols-1 gap-3 rounded-md border border-[#A855F7]/40 bg-[#A855F7]/5 p-3">
                  <p className="text-xs font-semibold text-[#A855F7] flex items-center gap-1"><ArrowRightLeft className="w-3.5 h-3.5" /> Transfert de balise</p>
                  <div>
                    <label className="label-mono block mb-1.5">Démontée du véhicule</label>
                    <select className={inp} value={form.source_vehicle_id} onChange={(e) => set("source_vehicle_id", e.target.value)} data-testid="f-source-vehicle">
                      <option value="">— Sélectionner —</option>
                      {refs.vehicles.map((v) => <option key={v.id} value={v.id}>{v.name} {v.plate ? `(${v.plate})` : ""}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="label-mono block mb-1.5">Remontée sur le véhicule</label>
                    <select className={inp} value={form.dest_vehicle_id} onChange={(e) => set("dest_vehicle_id", e.target.value)} data-testid="f-dest-vehicle">
                      <option value="">— Sélectionner —</option>
                      {refs.vehicles.map((v) => <option key={v.id} value={v.id}>{v.name} {v.plate ? `(${v.plate})` : ""}</option>)}
                    </select>
                  </div>
                </div>
              ) : (
                <div>
                  <label className="label-mono block mb-1.5">Véhicule concerné</label>
                  <select className={inp} value={form.vehicle_id} onChange={(e) => set("vehicle_id", e.target.value)} data-testid="f-vehicle">
                    <option value="">— Sélectionner —</option>
                    {refs.vehicles.map((v) => <option key={v.id} value={v.id}>{v.name} {v.plate ? `(${v.plate})` : ""}</option>)}
                  </select>
                </div>
              )}

              <div>
                <label className="label-mono block mb-1.5">Notes</label>
                <textarea className={inp} rows={3} value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Détails de l'intervention…" data-testid="f-notes" />
              </div>

              {/* Photos */}
              <div>
                <label className="label-mono block mb-1.5">Photos</label>
                <div className="flex flex-wrap gap-2">
                  {form.photos.map((p, i) => (
                    <div key={i} className="relative">
                      <img src={fileUrl(p.path)} alt="" className="w-16 h-16 rounded-md object-cover border border-border" />
                      <button onClick={() => removePhoto(i)} data-testid={`remove-photo-${i}`} className="absolute -top-1.5 -right-1.5 bg-destructive text-white rounded-full p-0.5"><X className="w-3 h-3" /></button>
                    </div>
                  ))}
                  <label className="w-16 h-16 rounded-md border-2 border-dashed border-border flex items-center justify-center cursor-pointer hover:bg-secondary/40" data-testid="add-photo">
                    {uploadingPhoto ? <Loader2 className="w-5 h-5 animate-spin" /> : <Camera className="w-5 h-5 text-muted-foreground" />}
                    <input type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={addPhotos} />
                  </label>
                </div>
              </div>

              {/* Localisation */}
              <div>
                <label className="label-mono block mb-1.5">Lieu d'intervention</label>
                <button type="button" onClick={grabLocation} data-testid="grab-location" className="flex items-center gap-2 text-sm px-3 py-2 rounded-md border border-border hover:bg-secondary/50">
                  {geoLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4 brand-teal" />} Utiliser ma position
                </button>
                {form.location && <p className="text-xs text-muted-foreground mt-1">{form.location.address || `${form.location.lat?.toFixed(5)}, ${form.location.lon?.toFixed(5)}`}</p>}
              </div>

              {/* Signature */}
              <div>
                <label className="label-mono block mb-1.5">Signature client</label>
                <SignaturePad value={form.signature} onChange={(v) => set("signature", v)} />
              </div>

              {editId && (
                <div className="flex gap-2">
                  <button onClick={() => window.open(pdfHref(editId), "_blank")} data-testid="modal-pdf-btn" className="flex-1 border border-border rounded-md py-2.5 text-sm font-semibold flex items-center justify-center gap-2 hover:bg-secondary/50"><FileDown className="w-4 h-4" /> Bon PDF</button>
                  <button onClick={() => sendReport({ id: editId })} data-testid="modal-send-btn" className="flex-1 border border-[#F5821F]/50 text-[#F5821F] rounded-md py-2.5 text-sm font-semibold flex items-center justify-center gap-2 hover:bg-[#F5821F]/10"><Send className="w-4 h-4" /> Envoyer au client</button>
                </div>
              )}
              <button onClick={save} disabled={saving} data-testid="save-intervention" className="w-full bg-[#F5821F] text-white font-semibold py-3 rounded-md flex items-center justify-center gap-2 disabled:opacity-50">
                {saving && <Loader2 className="w-4 h-4 animate-spin" />} {editId ? "Enregistrer" : "Créer l'intervention"}
              </button>
            </div>
          </div>
        </div>
      )}
      {viewRow && (
        <div className="fixed inset-0 z-[1000] bg-black/60 flex items-end sm:items-center justify-center" onClick={() => setViewRow(null)}>
          <div className="bg-background w-full sm:max-w-lg sm:rounded-xl rounded-t-2xl max-h-[92vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()} data-testid="intervention-view-modal">
            <div className="flex items-center justify-between mb-4 sticky top-0 bg-background pb-2">
              <h2 className="text-lg font-bold">Intervention</h2>
              <button onClick={() => setViewRow(null)} data-testid="close-view-modal"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-3 text-sm" data-testid="intervention-view-body">
              <div className="flex flex-wrap gap-1.5"><Badge item={meta(TYPES, viewRow.type)} /><Badge item={meta(STATUS, viewRow.status)} /></div>
              {viewRow.company_name && <div className="flex items-center gap-2"><Building2 className="w-4 h-4 text-muted-foreground" /> {viewRow.company_name}</div>}
              {(viewRow.device_imei || viewRow.device_name) && <div className="flex items-center gap-2"><Cpu className="w-4 h-4 text-muted-foreground" /> {viewRow.device_name || "Balise"} {viewRow.device_imei ? `· ${viewRow.device_imei}` : ""}</div>}
              {viewRow.type === "transfert" ? (
                <div className="flex items-center gap-2 flex-wrap"><ArrowRightLeft className="w-4 h-4 text-muted-foreground" /> {viewRow.source_vehicle_name || "?"} → {viewRow.dest_vehicle_name || "?"}</div>
              ) : viewRow.vehicle_name ? (
                <div className="flex items-center gap-2"><Truck className="w-4 h-4 text-muted-foreground" /> {viewRow.vehicle_name}</div>
              ) : null}
              <div className="flex items-center gap-2 text-muted-foreground"><Calendar className="w-4 h-4" /> {fmt(viewRow.date)} · {viewRow.technician_name || "—"}</div>
              {viewRow.notes && <div className="rounded-md border border-border p-3 whitespace-pre-wrap">{viewRow.notes}</div>}
              {(viewRow.photos?.length || viewRow.signature) && (
                <div className="flex gap-1.5 flex-wrap">
                  {(viewRow.photos || []).map((p, i) => <img key={i} src={fileUrl(p.path)} alt="" className="w-16 h-16 rounded object-cover border border-border" />)}
                  {viewRow.signature?.path && <img src={fileUrl(viewRow.signature.path)} alt="signature" className="w-16 h-16 rounded object-contain border border-border bg-white" />}
                </div>
              )}
              <button onClick={() => window.open(pdfHref(viewRow.id), "_blank")} data-testid="view-pdf-btn" className="w-full border border-border rounded-md py-2.5 text-sm font-semibold flex items-center justify-center gap-2 hover:bg-secondary/50"><FileDown className="w-4 h-4" /> Consulter le bon PDF</button>
            </div>
          </div>
        </div>
      )}
      <InstallWizard open={wizardOpen} onClose={() => setWizardOpen(false)} companies={refs.companies} resumeId={resumeId} onFinished={onWizardFinished} />
    </Layout>
  );
}
